import type { UserType } from "@volgo/core/contract";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { type ApiClient, isSessionRejection } from "./api/client";

export type SessionState =
  | { status: "loading"; user: null }
  | { status: "anonymous"; user: null }
  | { status: "unavailable"; user: null; error: unknown }
  | { status: "authenticated"; user: UserType };

interface SessionApi {
  state: SessionState;
  client: ApiClient;
  loginWithKey(key: string): Promise<void>;
  logout(): Promise<void>;
  retry(): void;
}

const Ctx = createContext<SessionApi | null>(null);

export function useSession(): SessionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

/** Holds who is logged in. On start it asks `auth.me`; the client refreshes the token itself if a cookie exists. */
export function SessionProvider({ client, children }: { client: ApiClient; children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading", user: null });

  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: retry explicitly reruns session bootstrap.
  useEffect(() => {
    setState({ status: "loading", user: null });
    let alive = true;
    client
      .call("auth.me")
      .then(({ user }) => alive && setState({ status: "authenticated", user }))
      .catch((error: unknown) => {
        if (alive)
          setState(
            isSessionRejection(error)
              ? { status: "anonymous", user: null }
              : { status: "unavailable", user: null, error },
          );
      });
    const off = client.onTokenChange((t) => {
      if (t === null) setState({ status: "anonymous", user: null });
    });
    return () => {
      alive = false;
      off();
    };
  }, [client, attempt]);

  const loginWithKey = useCallback(
    async (key: string) => {
      const session = await client.call("auth.keyLogin", { body: { key } });
      setState({ status: "authenticated", user: session.user });
    },
    [client],
  );
  const logout = useCallback(async () => {
    await client.call("auth.logout");
    setState({ status: "anonymous", user: null });
  }, [client]);

  const value = useMemo(
    () => ({ state, client, loginWithKey, logout, retry }),
    [state, client, loginWithKey, logout, retry],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
